/**
 * Pieces of the dashboard that are big enough to name but not to file
 * separately: the chase table, the safety rows, and the quick-action buttons.
 */

import { duesState } from '../../data';
import { useUI } from '../../context/UIContext';
import {
  EventSheet,
  ResultSheet,
  SafetyContactSheet,
  VolunteerHoursSheet,
} from '../../components/sheets/ClubSheets';
import { Button, Pill, Table } from '../../components/ui';

/**
 * The six members most worth chasing, with a one-tap pay link.
 *
 * Two renderings of the same rows. A four-column table is unreadable at 360px
 * — the bib and the status pill get crushed to fit the buttons — so below
 * `sm:` each member becomes a card with the two actions on their own line,
 * where a thumb can actually hit them.
 */
export function DuesChaseTable({
  members,
  onOpen,
  onPayLink,
  onCash,
}) {
  /** The pay-link / cash pair. `stop` keeps a tap off the row navigation. */
  function Actions({ m, block }) {
    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn(m);
    };
    return (
      <div className={`flex gap-2 ${block ? '[&>button]:flex-1' : 'justify-end'}`}>
        <Button tone="p" small onClick={stop(onPayLink)}>
          Pay link
        </Button>
        <Button tone="g" small onClick={stop(onCash)}>
          Cash
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* phone: one card per member */}
      <div className="grid gap-px border border-line bg-line sm:hidden">
        {members.map((m) => {
          const dues = duesState(m);
          return (
            <div
              key={m.id}
              className="cursor-pointer bg-white p-4"
              onClick={() => onOpen(m.id)}
              role="presentation"
            >
              <div className="flex items-start gap-3">
                <span className="font-market text-[17px] font-semibold tabular-nums text-copy-label">
                  {m.bib}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-market text-[14.5px] font-bold text-ink-700">{m.name}</div>
                  <div className="mt-1">
                    <Pill tone={dues.cls}>{dues.label}</Pill>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <Actions m={m} block />
              </div>
            </div>
          );
        })}
      </div>

      {/* desktop: the table */}
      <div className="max-sm:hidden">
        <Table
          head={
            <tr>
              <th>Bib</th>
              <th>Member</th>
              <th>Status</th>
              <th />
            </tr>
          }
        >
          {members.map((m) => {
            const dues = duesState(m);
            return (
              <tr
                key={m.id}
                className="cursor-pointer hover:bg-ground-warm"
                onClick={() => onOpen(m.id)}
              >
                <td>{m.bib}</td>
                <td className="font-market font-bold text-ink-700">{m.name}</td>
                <td>
                  <Pill tone={dues.cls}>{dues.label}</Pill>
                </td>
                <td>
                  <Actions m={m} />
                </td>
              </tr>
            );
          })}
        </Table>
      </div>
    </>
  );
}

/**
 * The club safety contact every member sees on their emergency card, plus a
 * warning when anyone on the roster has no emergency contact on file. That
 * second row is the one that matters — this is a running club.
 */
export function SafetyRows({ db }) {
  const { openSheet } = useUI();
  const missing = db.members.filter((m) => !m.emPhone);

  return (
    <>
      {/* Ink, not red: this is the number every member carries on every run,
          so it gets the weight of a plaque rather than the look of an error. */}
      <div className="flex flex-wrap items-center gap-3 bg-[linear-gradient(120deg,#161C2C,#0B0F18)] px-4 py-3.5 text-white">
        <div className="min-w-0 flex-1">
          <div className="font-market text-[9.5px] font-bold uppercase tracking-[0.22em] text-gold-500">
            Emergency contact — on every run
          </div>
          <div className="mt-1.5 font-market text-[18px] font-extrabold tracking-[-0.025em] [overflow-wrap:anywhere]">
            {db.meta.sosName || 'Not set'}
          </div>
          <div className="mt-1 font-market text-[13px] tabular-nums text-white/55">
            {db.meta.sosPhone || '—'}
          </div>
        </div>
        <button
          type="button"
          className="flex-none border border-white/[.18] bg-white/[.05] px-3.5 py-2 font-market text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors duration-200 hover:border-gold-500/60 hover:text-gold-500"
          onClick={() =>
            openSheet(
              <SafetyContactSheet name={db.meta.sosName ?? ''} phone={db.meta.sosPhone ?? ''} />,
            )
          }
        >
          Change
        </button>
      </div>

      {missing.length > 0 && (
        <div className="mt-2.5 flex items-center gap-3 border border-gold-300 bg-gold-50 px-3.5 py-3">
          <div className="grid h-[30px] w-[30px] flex-none place-items-center bg-flag-500 font-market text-sm font-bold text-white">
            !
          </div>
          <div className="text-[12.5px] leading-relaxed text-copy">
            <b className="font-semibold">{missing.length} member(s)</b> missing an emergency
            contact. Runners must not run without one.
          </div>
        </div>
      )}
    </>
  );
}

/** The three non-member quick actions. Add member lives on the dashboard. */
export function ClubSheetsQuickActions({ db }) {
  const { openSheet } = useUI();
  return (
    <>
      <Button tone="d" onClick={() => openSheet(<ResultSheet db={db} />)}>
        + Race result
      </Button>
      {/* Uniform dark: these are all "create" actions of equal weight. Red is
          reserved for destructive buttons now. */}
      <Button tone="d" onClick={() => openSheet(<EventSheet />)}>
        + Event / meeting
      </Button>
      <Button tone="d" onClick={() => openSheet(<VolunteerHoursSheet db={db} />)}>
        + Volunteer hours
      </Button>
    </>
  );
}
